// Source citation and risk disclaimer, shown next to any figure we publish.
//
// This is not decoration. Publishing fund analysis in India can be read as investment advice, which
// is regulated, so the page has to say plainly that it is neither advice nor a recommendation, that
// we are not registered to give either, and where every number came from. The wording is served by
// the API rather than written here, so one edit changes it everywhere instead of leaving a stale
// copy on whichever page nobody remembered.
import { useState } from 'react';
import { ExternalLink, ShieldAlert } from 'lucide-react';

// Order matters: the two that carry legal weight come first, so they survive a reader who stops
// after one line.
const ORDER = ['not_advice', 'not_registered', 'market_risk', 'past_performance', 'accuracy', 'liability'];

export default function DataDisclaimer({ source, disclaimer, extra, className = '' }) {
  const [open, setOpen] = useState(false);
  if (!disclaimer && !source) return null;

  const lines = ORDER.filter((k) => disclaimer?.[k]).map((k) => disclaimer[k]);
  const lead = lines[0];
  const rest = lines.slice(1);

  return (
    <aside className={`rounded-xl border border-slate-200 bg-slate-50 p-4 text-xs text-slate-600 ${className}`}
           data-testid="data-disclaimer">
      <p className="flex items-start gap-2 font-semibold text-slate-800">
        <ShieldAlert className="w-4 h-4 shrink-0 mt-px text-slate-500" />
        <span>{lead}</span>
      </p>

      {extra && <p className="mt-2">{extra}</p>}

      {source?.name && (
        <p className="mt-2">
          Source:{' '}
          {source.url ? (
            <a href={source.url} target="_blank" rel="noopener noreferrer nofollow"
               className="underline hover:text-slate-900 inline-flex items-center gap-1">
              {source.name} <ExternalLink className="w-3 h-3" />
            </a>
          ) : source.name}
          {source.basis ? `. ${source.basis}.` : '.'}
          {source.licence ? ` ${source.licence}.` : ''}
        </p>
      )}

      {rest.length > 0 && (
        <>
          <button onClick={() => setOpen((o) => !o)}
                  className="mt-2 underline font-medium hover:text-slate-900"
                  aria-expanded={open}>
            {open ? 'Hide full disclaimer' : 'Read the full disclaimer'}
          </button>
          {open && (
            <ul className="mt-2 space-y-1.5 list-disc pl-4">
              {rest.map((l) => <li key={l}>{l}</li>)}
            </ul>
          )}
        </>
      )}
    </aside>
  );
}
